import React, { createContext, useState, useEffect, useContext } from 'react';

const NetworkContext = createContext();

export function NetworkProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkQuality, setNetworkQuality] = useState('good');
  const [pendingOperations, setPendingOperations] = useState([]);
  const [retryCount, setRetryCount] = useState({});

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      processPendingOperations();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkQuality('offline');
    };

    // Monitor network quality
    const checkNetworkQuality = async () => {
      if (!navigator.onLine) {
        setNetworkQuality('offline');
        return;
      }

      try {
        const startTime = performance.now();
        const response = await fetch('/ping', { 
          method: 'HEAD',
          cache: 'no-cache'
        });
        const endTime = performance.now();
        const latency = endTime - startTime;

        if (latency < 100) {
          setNetworkQuality('good');
        } else if (latency < 500) {
          setNetworkQuality('fair');
        } else {
          setNetworkQuality('poor');
        }
      } catch (error) {
        setNetworkQuality('poor');
      }
    };

    // Check network quality every 30 seconds
    const qualityInterval = setInterval(checkNetworkQuality, 30000);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(qualityInterval);
    };
  }, []);

  // Process pending operations when back online
  const processPendingOperations = async () => {
    const operations = [...pendingOperations];
    for (const operation of operations) {
      try {
        await operation.retry();
        setPendingOperations(prev => prev.filter(op => op.id !== operation.id));
      } catch (error) {
        console.error('Failed to process pending operation:', error);
        // Keep failed operations for next retry
      }
    }
  };

  // Add operation to pending queue
  const addPendingOperation = (operation) => {
    setPendingOperations(prev => [...prev, operation]);
  };

  // Retry failed operation
  const retryOperation = async (operationId, maxRetries = 3) => {
    const operation = pendingOperations.find(op => op.id === operationId);
    if (!operation) return;

    const currentRetries = retryCount[operationId] || 0;
    if (currentRetries >= maxRetries) {
      setPendingOperations(prev => prev.filter(op => op.id !== operationId));
      return;
    }

    setRetryCount(prev => ({ ...prev, [operationId]: currentRetries + 1 }));
    try {
      await operation.retry();
      setPendingOperations(prev => prev.filter(op => op.id !== operationId));
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  return (
    <NetworkContext.Provider value={{
      isOnline,
      networkQuality,
      pendingOperations,
      addPendingOperation,
      retryOperation,
      processPendingOperations
    }}>
      {children}
    </NetworkContext.Provider>
  );
}

export const useNetwork = () => useContext(NetworkContext); 